/**
 * Material-UI Component Overrides
 * Defines default styles and variants for MUI components
 * Ensures consistency across the entire application
 */

export const components = {
  // Button component
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '8px 20px',
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.01em',
        boxShadow: 'none',
        transition:
          'background-color 150ms cubic-bezier(0.2,0,0,1), box-shadow 200ms cubic-bezier(0.2,0,0,1), border-color 150ms cubic-bezier(0.2,0,0,1), color 150ms cubic-bezier(0.2,0,0,1)',
        '&:hover': {
          boxShadow: 'none',
        },
        // Consistent soft focus ring in the muted primary
        '&.Mui-focusVisible': {
          boxShadow: '0 0 0 3px rgba(108, 142, 191, 0.24)',
        },
      },
      contained: {
        // No jump on hover; a whisper of elevation only
        '&:hover': {
          boxShadow: '0 1px 3px rgba(16,24,32,0.06)',
        },
      },
      sizeLarge: {
        padding: '10px 24px',
        fontSize: '1rem',
      },
      sizeSmall: {
        padding: '6px 16px',
        fontSize: '0.8125rem',
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },

  // Card component
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: '1px solid #e6e8e4',
        boxShadow: '0 1px 2px rgba(16,24,32,0.04)',
        transition: 'box-shadow 200ms cubic-bezier(0.2,0,0,1)',
        '&:hover': {
          boxShadow: '0 2px 6px rgba(16,24,32,0.07)',
        },
      },
    },
  },

  // Paper component
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
      elevation1: {
        boxShadow: '0 1px 3px rgba(16,24,32,0.06)',
      },
      elevation2: {
        boxShadow: '0 2px 6px rgba(16,24,32,0.07)',
      },
      elevation3: {
        boxShadow: '0 4px 10px rgba(16,24,32,0.08)',
      },
    },
  },

  // Chip component
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
        fontSize: '0.8125rem',
      },
    },
  },

  // TextField component
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'medium',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  },

  // Dialog component
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      },
    },
  },

  // Table components
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
      },
      head: {
        fontWeight: 600,
        backgroundColor: '#f5f5f5',
      },
    },
  },

  // Tooltip component
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: 'rgba(33, 33, 33, 0.95)',
        borderRadius: 6,
        fontSize: '0.75rem',
        padding: '8px 12px',
      },
      arrow: {
        color: 'rgba(33, 33, 33, 0.95)',
      },
    },
  },

  // AppBar component
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      },
    },
  },

  // Drawer component
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
      },
    },
  },

  // IconButton component
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      },
    },
  },

  // Alert component
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontSize: '0.875rem',
      },
      standardSuccess: {
        backgroundColor: '#e8f5e9',
        color: '#1b5e20',
      },
      standardError: {
        backgroundColor: '#ffebee',
        color: '#b71c1c',
      },
      standardWarning: {
        backgroundColor: '#fff3e0',
        color: '#e65100',
      },
      standardInfo: {
        backgroundColor: '#e1f5fe',
        color: '#01579b',
      },
    },
  },

  // Tabs component
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        minHeight: 48,
      },
    },
  },

  // Switch component — keep MUI's native SIZING (a previous custom size
  // override collided with `size="small"` and made the thumb bulge). Only
  // adjust COLORS/shadow here so the off-state reads clearly on white:
  // a solid grey track + a defined white thumb.
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.Mui-checked + .MuiSwitch-track': {
          backgroundColor: '#6C8EBF',
          opacity: 1,
        },
      },
      track: {
        backgroundColor: '#b6bab4',
        opacity: 1,
      },
      thumb: {
        color: '#ffffff',
        boxShadow: '0 1px 2px rgba(16,24,32,0.35), 0 0 0 1px rgba(16,24,32,0.06)',
      },
    },
  },
};
